import React, { useState } from "react";

import { Box, Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { useSnackBar } from "@/hooks/useSnackBar";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";
import { EditIconButton } from "@components/IconButtons";
import { NCIcon } from "@components/Icons/Icon";

import EditLifeStyleModal from "./components/EditLifestyleModal";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()({
  section: {
    padding: "24px",
    backgroundColor: colors.white,
    width: "100%",
  },
  spacing: {
    padding: "4px 12px",
    display: "inline-block",
    fontWeight: 700,
  },
});

const lifestyleFragment = graphql(`
  fragment LifestyleSection on Client {
    id
    lifestyle {
      id
      ...ClientLifestyle
    }
  }

  fragment ClientLifestyle on Lifestyle {
    preferredCoachingType
    preferredCoachingTypeDescription
    physicalActivityLevel2
    stressLevel
    sportsDescription
    sportsFrequency
    workoutDescription
    workoutFrequency
    doYouCook
    doYouDrink
    alcoholIntakeFrequency
    averageHoursOfSleep
    bowelMovement
  }
`);

const Lifestyle: React.FC<Props> = ({ clientId }) => {
  const { classes } = useStyles();
  const [client] = useFragment(lifestyleFragment, `Client:${clientId}`);
  const lifestyle = client?.lifestyle;
  const coachingType = lifestyle?.preferredCoachingType;
  const activityLevel = lifestyle?.physicalActivityLevel2;
  const stressLevel = lifestyle?.stressLevel ?? "";
  const sportsDesc = lifestyle?.sportsDescription;
  const alcoholIntake = lifestyle?.alcoholIntakeFrequency;
  const doyouDrink = alcoholIntake ? "Yes" : "No";
  const workOut = lifestyle?.workoutDescription;
  const hoursOfSleep = lifestyle?.averageHoursOfSleep;
  const hoursText = hoursOfSleep == 1 ? "hour" : "hours";
  const [modalVisibility, setModalVisibility] = useState<boolean>();
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();

  const stressLevelDescriptions: { [key: string]: string } = {
    ACUTE:
      "Acute stress is usually brief. It is the most common and frequent presentation. " +
      "Acute stress is most often caused by reactive thinking. Acute stress can present in anyone’s life. It is highly treatable and manageable.",
    EPISODIC:
      "The individuals who frequently suffer acute stress often live a life of chaos and crisis. " +
      "They are always in a rush or feel pressured. They take on many responsibilities, and usually can not stay organized with so many time demands. " +
      "These individuals are perpetually in the grips of acute stress overload. Episodic acute stress leads to more pronounced health issues such as, " +
      "high blood pressure and heart disease, and irritable bowel syndrome (IBS).",
    CHRONIC:
      "Chronic stress is the most harmful type of stress. If chronic stress is left untreated over a long period of time, " +
      "it can significantly and often irreversibly damage your physical health and deteriorate your mental health. Behavioral actions and " +
      "emotional reactions become ingrained. There is change in the hardwiring of the neurobiology of the brain and body. " +
      "There by making them constantly prone to the hazardous stress effects on the body + mind+ cognitive regardless of the scenarios.",
  };

  const handleModalClose = () => {
    setModalVisibility(false);
  };

  const handleModalOpen = () => {
    setModalVisibility(true);
  };

  const handleSnackBar = (isSuccessful: boolean) => {
    if (isSuccessful) {
      snackBarHelper.open("Successfully updated");
    } else {
      snackBarHelper.open(
        "There is an error in updating your clients lifestyle information"
      );
    }
  };

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={5}
        mb={3}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Lifestyle
        </Typography>
        <Box marginY={3} />
        <EditIconButton onClick={handleModalOpen} />
      </Box>
      <section className={classes.section}>
        <Grid container spacing={3}>
          <Grid container item spacing={3} xs={12}>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon icon="person" htmlColor={colors.grey} mr="normal" />
                <Typography variant="body1">Preferred coaching type</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
              >
                {coachingType ? humanize(coachingType || "") : "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon
                  icon="person-with-leaves"
                  htmlColor={colors.grey}
                  mr="normal"
                />
                <Typography variant="body1">Activity level</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
              >
                {activityLevel ? humanize(activityLevel || "") : "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon
                  icon="person-walk"
                  htmlColor={colors.grey}
                  mr="normal"
                />
                <Typography variant="body1">Workout</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
                gutterBottom
              >
                {workOut ? humanize(workOut || "") : "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Grid container item spacing={4} xs={12}>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon
                  icon="basket-ball"
                  htmlColor={colors.grey}
                  mr="normal"
                />
                <Typography variant="body1">Sports</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
                gutterBottom
              >
                {sportsDesc ?? "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon icon="person" htmlColor={colors.grey} mr="normal" />
                <Typography variant="body1">Do you drink?</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
                gutterBottom
              >
                {doyouDrink ? humanize(doyouDrink || "") : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon icon="person-bed" htmlColor={colors.grey} mr="normal" />
                <Typography variant="body1">Sleep pattern</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
                gutterBottom
              >
                {hoursOfSleep
                  ? humanize(`About ${hoursOfSleep} ${hoursText}` || "")
                  : "N/A"}
              </Typography>
              {Features.sleepPatternSubtextEnabled && (
                <Typography variant="body2" color="textSecondary">
                  Lorem Ipsum is simply dummy text of the printing and type
                  setting industry.
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid container item spacing={4} xs={12}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <NCIcon icon="virus-icon" htmlColor={colors.grey} mr="normal" />
                <Typography variant="body1">Stress level</Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.spacing}
              >
                {stressLevel ? humanize(stressLevel || "") : "N/A"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stressLevelDescriptions[stressLevel]}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <EditLifeStyleModal
          visible={modalVisibility}
          onClose={handleModalClose}
          showSnackBar={handleSnackBar}
          lifestyle={lifestyle}
          stressLevelDescriptions={stressLevelDescriptions}
        />
      </section>
      <SimpleSnackBar />
    </>
  );
};

export default Lifestyle;
